import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertOctagon, ArrowDown, ArrowRight, ArrowUp, Boxes, UserMinus, CalendarClock, ChevronDown, ChevronRight, Clock, Crown, Download, ExternalLink, Mail,
  MessageSquarePlus, Search, Send, Sparkles, UserRound, Workflow,
} from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";
import { useAuth, hasRole } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError, formatINR, formatDate } from "@/lib/api";
import OrderFlowchart from "@/components/OrderFlowchart";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// ---- design tokens --------------------------------------------------------------------------
// One restrained palette: ink + slate neutrals, brand orange as the single accent, and red / amber
// reserved for urgency. Customer tier is the only other colour, kept to a thin rail and a small chip.
const TIER = {
  gold:    { label: "Gold",    chip: "bg-amber-100 text-amber-800 ring-amber-300",       rail: "bg-amber-400",   row: "border-l-4 border-l-amber-400" },
  silver:  { label: "Silver",  chip: "bg-slate-100 text-slate-700 ring-slate-300",       rail: "bg-slate-400",   row: "border-l-4 border-l-slate-300" },
  new:     { label: "New",     chip: "bg-emerald-100 text-emerald-800 ring-emerald-300", rail: "bg-emerald-500", row: "border-l-4 border-l-emerald-400" },
  regular: { label: "Regular", chip: "bg-blue-100 text-blue-800 ring-blue-300",          rail: "bg-blue-500",    row: "border-l-4 border-l-blue-300" },
};

const BUCKETS = [
  { key: "today",         label: "Due today",       dot: "bg-rose-500" },
  { key: "soon",          label: "Due in 1–3 days", dot: "bg-amber-400" },
  { key: "overdue_week",  label: "Overdue 1–7 d",   dot: "bg-orange-500" },
  { key: "overdue_month", label: "Overdue 8–30 d",  dot: "bg-red-600" },
  { key: "stale",         label: "Stale > 30 d",    dot: "bg-slate-400" },
];

const STAGE_SHORT = { bpr: "Plan", printing: "Print", avt: "AVT", slitting: "Slit", packing: "Pack", dispatch: "Disp" };
const STAGE_ORDER = ["bpr", "printing", "avt", "slitting", "packing", "dispatch"];
const STAGE_FILTERS = [
  { key: "bpr", label: "Planning" }, { key: "printing", label: "Printing" }, { key: "avt", label: "AVT Inspection" },
  { key: "slitting", label: "Slitting" }, { key: "packing", label: "Packing" }, { key: "dispatch", label: "Dispatch" },
];
const PAGE = 24;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// The financial year runs April to March, so the month buttons are laid out that way.
const FY_MONTHS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2];
const fyOf = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
};
const fyLabel = (y) => `FY ${y}-${String(y + 1).slice(2)}`;
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
// The backend reads "last complete calendar month" as the one before the board's own date's month
// (see churn in daily_dashboard.py), so a finished month's own figures only show up when the board date
// sits just after it, not on its own last day.
const dayAfterMonth = (y, m) => new Date(y, m + 1, 1);
const REASON_PRESETS = ["Waiting for customer artwork approval", "Material short at stores", "Machine breakdown",
  "Die / plate not ready", "QC rejected, reprint", "Awaiting dispatch confirmation"];

function TierChip({ tier, className = "" }) {
  const t = TIER[tier] || TIER.regular;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${t.chip} ${className}`}>
      {tier === "gold" && <Crown size={10} />}
      {tier === "new" && <Sparkles size={10} />}
      {t.label}
    </span>
  );
}

function DueBadge({ days }) {
  if (days === 0) return <span className="rounded-full px-2 py-0.5 text-xs font-bold bg-rose-600 text-white">Today</span>;
  if (days > 0) return <span className="rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-900">in {days}d</span>;
  return <span className="rounded-full px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700">{-days}d late</span>;
}

// Six-segment progress bar through Plan, Print, AVT, Slit, Pack, Disp.
function StageProgress({ stage, hot }) {
  const idx = STAGE_ORDER.indexOf(stage);
  return (
    <div>
      <div className="flex gap-1">
        {STAGE_ORDER.map((k, i) => (
          <div key={k} title={STAGE_SHORT[k]}
               className={`h-1.5 flex-1 rounded-full ${i < idx ? "bg-emerald-500" : i === idx ? (hot ? "bg-red-500 animate-pulse" : "bg-blue-500") : "bg-slate-200"}`} />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-slate-400 font-medium">
        <span>Plan</span><span>Print</span><span>AVT</span><span>Slit</span><span>Pack</span><span>Disp</span>
      </div>
    </div>
  );
}

const pill = (on) => `h-8 rounded-full px-3.5 text-xs font-semibold transition ${on
  ? "bg-slate-900 text-white shadow-sm" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:ring-slate-400 hover:text-slate-900"}`;

const CARD = "rounded-xl border border-slate-200/80 bg-white shadow-sm";

// Remembers each section's open/closed state between visits.
function useOpen(id, initial) {
  const key = `daily.open.${id}`;
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(key); return v == null ? initial : v === "1"; } catch { return initial; }
  });
  const toggle = () => setOpen((o) => {
    try { localStorage.setItem(key, o ? "0" : "1"); } catch { /* storage unavailable */ }
    return !o;
  });
  return [open, toggle];
}

function Collapse({ id, title, icon, summary, defaultOpen = false, testId, children }) {
  const [open, toggle] = useOpen(id, defaultOpen);
  return (
    <section className={CARD} data-testid={testId}>
      <button type="button" onClick={toggle} aria-expanded={open} className="flex w-full items-center gap-3 p-4 text-left">
        {open ? <ChevronDown size={18} className="shrink-0" /> : <ChevronRight size={18} className="shrink-0" />}
        {icon}
        <span className="font-display font-bold text-lg text-slate-900">{title}</span>
        {summary && <span className="ml-auto truncate text-xs text-slate-500">{summary}</span>}
      </button>
      {open && <div className="border-t border-slate-100 px-5 pb-5 pt-4">{children}</div>}
    </section>
  );
}

// One job tile: used both for the filtered board grid and for a job found by search (which may sit
// outside every filter — dispatched, or not due for months — so it carries its own "elsewhere" note).
function JobCard({ j, escalateDays, onClick }) {
  const late = j.days_to_due < 0;
  const stale = j.bucket === "stale";
  const critical = j.days_to_due <= 0 && !stale;
  const idle = j.days_since_update;
  const hot = idle > 7;
  const t = TIER[j.tier] || TIER.regular;
  return (
    <div role="button" tabIndex={0} data-testid="daily-job-row"
         onClick={onClick} onKeyDown={(e) => e.key === "Enter" && onClick()}
         className="group relative flex cursor-pointer flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A00]">
      <div className={`absolute inset-y-0 left-0 w-1.5 ${t.rail}`} />
      <div className="flex flex-1 flex-col gap-3 py-4 pl-5 pr-4">
        <div className="flex items-center gap-2">
          <span className="font-mono-num text-sm font-bold text-slate-900">{j.bpr}</span>
          <TierChip tier={j.tier} />
          {j.closed && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 ring-1 ring-inset ring-emerald-200">
              {j.status || "Closed"}
            </span>
          )}
          {!j.closed && j.escalated && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-700 ring-1 ring-inset ring-orange-200" title={`Idle ${escalateDays}+ days and late`}>
              <AlertOctagon size={10} /> Escalated
            </span>
          )}
          {!j.closed && (critical || stale) && (
            <span className={`${j.escalated ? "" : "ml-auto"} inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${stale ? "bg-slate-100 text-slate-600" : "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200"}`}>
              {critical && <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />}
              {stale ? "Stale" : "Critical"}
            </span>
          )}
        </div>

        <div>
          <div className="truncate font-semibold text-slate-900" title={j.customer}>{j.customer}</div>
          <div className="mt-0.5 truncate text-xs text-slate-500" title={j.product}>
            {j.product} <span className="text-slate-300">·</span>{" "}
            <span className="font-mono-num font-semibold text-slate-700">{Number(j.qty || 0).toLocaleString("en-IN")} Qty</span>
          </div>
        </div>

        <StageProgress stage={j.current_stage} hot={!j.closed && hot} />

        <div className="flex flex-wrap gap-1.5 text-[11px] font-semibold">
          <span className={`rounded-full px-2.5 py-1 ring-1 ring-inset ${j.days_to_due == null ? "bg-slate-50 text-slate-500 ring-slate-200" : late ? "bg-red-50 text-red-700 ring-red-200" : j.days_to_due === 0 ? "bg-rose-600 text-white ring-rose-600" : "bg-amber-50 text-amber-800 ring-amber-200"}`}>
            Due: {j.days_to_due == null ? (j.due_date ? formatDate(j.due_date) : "not set") : late ? `${-j.days_to_due}d late` : j.days_to_due === 0 ? "today" : `in ${j.days_to_due}d`}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ring-1 ring-inset ${!j.closed && hot ? "bg-red-50 text-red-700 ring-red-200" : "bg-blue-50 text-blue-700 ring-blue-200"}`}>
            <Workflow size={11} /> {!j.closed && hot ? "Stuck" : "At"}: {j.current_label}{idle != null ? ` (${idle}d)` : ""}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ring-1 ring-inset ${!j.closed && hot ? "bg-orange-50 text-orange-700 ring-orange-200" : "bg-slate-50 text-slate-600 ring-slate-200"}`}>
            <Clock size={11} /> Idle: {idle != null ? `${idle} day${idle === 1 ? "" : "s"}` : "-"}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ring-1 ring-inset ${j.owner ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-400 ring-slate-200"}`}>
            <UserRound size={11} /> {j.owner || "Unassigned"}
          </span>
          {j.material_blocked && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-amber-800 ring-1 ring-inset ring-amber-200" title={j.material_name || ""}>
              <Boxes size={11} /> Waiting on material
            </span>
          )}
          {j.value != null && j.value > 0 && (
            <span className="rounded-full bg-slate-50 px-2.5 py-1 font-mono-num text-slate-600 ring-1 ring-inset ring-slate-200">{formatINR(j.value)}</span>
          )}
        </div>

        <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-2.5 text-[11px]">
          <span className="truncate text-slate-400">
            {j.manual_reason ? <span className="text-sky-700 font-medium">Reason: {j.manual_reason}</span> : "No reason recorded"}
          </span>
          <span className="flex shrink-0 items-center gap-1 font-semibold text-slate-400 transition group-hover:text-[#FF5A00]">
            View flowchart <ArrowRight size={12} className="transition group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </div>
  );
}

export default function DailyDashboard() {
  const [data, setData] = useState(null);
  const [asOf, setAsOf] = useState("");
  // Which month button is lit: set by the FY/month picker, cleared by "Latest data" / "Switch to today" /
  // the exact-date box. Kept apart from `asOf` because the board date sent to the backend for a finished
  // month is the day AFTER it (see dateForMonth), one day past the month itself.
  const [pickedMonth, setPickedMonth] = useState(null); // {fy, m} | null
  const [cfg, setCfg] = useState({ gold_pct: 10, silver_pct: 30, new_days: 30 });
  const [bucket, setBucket] = useState("active"); // active | stale | all | a bucket key
  const [tiers, setTiers] = useState([]);
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [showTiers, setShowTiers] = useState(false);
  const [custTier, setCustTier] = useState("all");
  const [selId, setSelId] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [saving, setSaving] = useState(false);
  const [notified, setNotified] = useState(false);
  const { user } = useAuth();
  const canDigest = hasRole(user, "production_manager");
  const [owners, setOwners] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("all"); // all | unassigned | a name
  const [onlyEsc, setOnlyEsc] = useState(false);
  const [materialFilter, setMaterialFilter] = useState(null);
  const [jobsOpen, toggleJobs] = useOpen("jobs", true);
  const [digest, setDigest] = useState(null);
  const [digestOpen, setDigestOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchHits, setSearchHits] = useState(null); // {q, total, jobs} once a wider search has run, else null
  const [searching, setSearching] = useState(false);
  const searchReq = useRef(0); // guards against a slow earlier search overwriting a faster later one

  const load = useCallback(() => {
    const params = { ...cfg };
    if (asOf) params.as_of = asOf;
    api.get("/dashboard/daily", { params })
      .then(({ data }) => {
        setData(data);
        if (!asOf) setAsOf(data.as_of);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [asOf, cfg]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/dashboard/daily/owners").then(({ data }) => setOwners(data)).catch(() => {}); }, []);
  useEffect(() => { setPage(1); }, [bucket, tiers, stage, q, asOf, ownerFilter, onlyEsc, materialFilter]);

  useEffect(() => {
    if (data && !notified) {
      setNotified(true);
      const { today, overdue_week } = data.buckets;
      if (today > 0) toast.warning(`${today} job(s) are due today and not yet dispatched`);
      else if (overdue_week > 0) toast.warning(`${overdue_week} job(s) became overdue in the last 7 days`);
    }
  }, [data, notified]);

  const toggleTier = (t) => setTiers((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  // Everything except the stage filter, so the stage chips can show counts for the current slice.
  const sliced = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.jobs.filter((j) => {
      if (bucket === "active" ? j.bucket === "stale" : bucket !== "all" && j.bucket !== bucket) return false;
      if (tiers.length && !tiers.includes(j.tier)) return false;
      if (onlyEsc && !j.escalated) return false;
      if (materialFilter && !(j.material_blocked && j.material_key === materialFilter)) return false;
      if (ownerFilter === "unassigned" ? j.owner : ownerFilter !== "all" && j.owner !== ownerFilter) return false;
      if (term && !`${j.bpr} ${j.customer} ${j.product}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [data, bucket, tiers, q, ownerFilter, onlyEsc, materialFilter]);

  const stageCounts = useMemo(() => {
    const c = {};
    sliced.forEach((j) => { c[j.current_stage] = (c[j.current_stage] || 0) + 1; });
    return c;
  }, [sliced]);

  const jobs = useMemo(() => (stage === "all" ? sliced : sliced.filter((j) => j.current_stage === stage)), [sliced, stage]);
  const pages = Math.max(1, Math.ceil(jobs.length / PAGE));
  const pageJobs = jobs.slice((page - 1) * PAGE, page * PAGE);

  // A typed BPR/customer/product that matches nothing in the current filters is searched for across
  // every job (any status, any due date) rather than left to report "nothing here" on a real job.
  const searchTerm = q.trim();
  const showingSearch = searchTerm.length >= 2 && jobs.length === 0;
  useEffect(() => {
    if (!showingSearch) { setSearchHits(null); return; }
    const mine = ++searchReq.current;
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/dashboard/daily/search", { params: { q: searchTerm, as_of: asOf || undefined } })
        .then(({ data }) => { if (mine === searchReq.current) setSearchHits(data); })
        .catch(() => { if (mine === searchReq.current) setSearchHits({ q: searchTerm, total: 0, jobs: [] }); })
        .finally(() => { if (mine === searchReq.current) setSearching(false); });
    }, 400);
    return () => clearTimeout(t);
  }, [showingSearch, searchTerm, asOf]);

  // A selected job may be one search found outside the board's own list (closed, or not due soon).
  const sel = useMemo(() => data?.jobs.find((j) => j.id === selId) || searchHits?.jobs.find((j) => j.id === selId) || null,
    [data, searchHits, selId]);
  const selectJob = (j) => { setSelId(j.id); setReasonText(j.manual_reason || ""); };

  const avg = useMemo(() => Object.fromEntries((data?.stage_stats || []).map((s) => [s.key, s.avg])), [data]);

  // Headline numbers for the hero: how many premium customers are waiting on a late job.
  const risk = useMemo(() => {
    if (!data) return { jobs: 0, customers: 0 };
    const late = data.jobs.filter((j) => j.days_to_due <= 0 && j.bucket !== "stale" && (j.tier === "gold" || j.tier === "silver"));
    return { jobs: late.length, customers: new Set(late.map((j) => j.customer)).size };
  }, [data]);

  const assignOwner = async (job, owner) => {
    try {
      await api.post("/dashboard/daily/owner", { job_id: job.id, owner: owner || null });
      const patch = (j) => (j.id === job.id ? { ...j, owner: owner || null } : j);
      setData((d) => ({ ...d, jobs: d.jobs.map(patch) }));
      setSearchHits((s) => (s ? { ...s, jobs: s.jobs.map(patch) } : s));
      toast.success(owner ? `Assigned to ${owner}` : "Owner cleared");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/dashboard/daily/board.pdf", { params: asOf ? { as_of: asOf } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `daily-dashboard-${asOf || "today"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not build the PDF");
    }
  };

  const openDigest = async () => {
    setDigestOpen(true); setDigest(null);
    try { setDigest((await api.get("/dashboard/digest/preview")).data); } catch (e) { toast.error(formatApiError(e)); setDigestOpen(false); }
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post("/dashboard/digest/send");
      toast.success(Object.values(r).join(" · "));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const saveReason = async () => {
    setSaving(true);
    try {
      const { data: entry } = await api.post("/dashboard/daily/reason", { job_id: sel.id, text: reasonText });
      const patch = (j) => (j.id === sel.id ? { ...j, manual_reason: entry.text, manual_reason_by: entry.by, manual_reason_at: entry.at } : j);
      setData((d) => ({ ...d, jobs: d.jobs.map(patch) }));
      setSearchHits((s) => (s ? { ...s, jobs: s.jobs.map(patch) } : s));
      toast.success("Reason saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-8 label-caps">Loading…</div>;

  const b = data.buckets;
  const flagged = Math.max(1, BUCKETS.reduce((n, k) => n + (b[k.key] || 0), 0));
  const lateTotal = b.overdue_week + b.overdue_month;
  const escCount = data.jobs.filter((j) => j.escalated).length;
  const otif = data.otif_trend || [];
  const otifHas = otif.some((w) => w.pct != null);
  const perf = data.performance;
  const hasPerf = perf.current.records > 0;
  const last4 = otif.slice(-4);
  const otif4 = last4.reduce((n, w) => n + w.total, 0)
    ? Math.round((100 * last4.reduce((n, w) => n + w.on_time, 0)) / last4.reduce((n, w) => n + w.total, 0)) : null;
  const wasteHas = perf.wastage_weekly.some((w) => w.wastage_pct != null);
  const churn = data.churn;
  const wipTotal = data.wip.reduce((n, w) => n + w.count, 0);
  const wipLate = data.wip.reduce((n, w) => n + w.overdue, 0);
  const lastWaste = [...perf.wastage_weekly].reverse().find((w) => w.wastage_pct != null);
  const monthName = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  // The performance tiles are a 30-day window ending at the board's as_of date (see _performance in
  // daily_dashboard.py) — the reports they link to open on that same window, not all-time.
  const perfTo = data.as_of;
  const perfFrom = isoOf(new Date(new Date(`${perfTo}T00:00:00`).getTime() - 29 * 86400000));
  const perfQS = `from=${perfFrom}&to=${perfTo}`;
  const tiles = [
    { label: "Good output", value: perf.current.good_pct, prev: perf.previous.good_pct, unit: "%", better: "up", to: `/reports?tab=stage-waste-trend&${perfQS}`, sub: "of metres run" },
    { label: "Wastage", value: perf.current.wastage_pct, prev: perf.previous.wastage_pct, unit: "%", better: "down", to: `/reports?tab=stage-waste-trend&${perfQS}`, sub: "of metres run" },
    { label: "Raw material used", value: perf.current.rm_used_pct, prev: perf.previous.rm_used_pct, unit: "%", better: "up", to: `/inventory?tab=movements&${perfQS}`, sub: "of metres issued" },
    { label: "Jobs printed / day", value: perf.current.jobs_per_day, prev: perf.previous.jobs_per_day, unit: "", better: "up", to: "/reports?tab=op-output", sub: "on days with printing" },
    { label: "On-time delivery", value: otif4, prev: null, unit: "%", better: "up", to: "/reports?tab=otif", sub: "last 4 weeks" },
  ];
  const viewingToday = asOf === data.today;
  // Financial year and month pickers: each one moves the board's date, which is what every figure is read against.
  // The lit month/year come from the explicit pick, not from `asOf` itself (see dateForMonth below).
  const asOfFy = pickedMonth ? pickedMonth.fy : fyOf(asOf || data.today);
  const asOfMonth = pickedMonth ? pickedMonth.m : new Date(`${asOf || data.today}T00:00:00`).getMonth();
  const todayDate = new Date(`${data.today}T00:00:00`);
  const fyYears = (() => {
    const first = fyOf(data.earliest_data_date || data.today);
    const last = fyOf(data.today);
    return Array.from({ length: Math.max(1, last - first + 1) }, (_, i) => last - i);
  })();
  // A finished month is read the day after it closes (so churn and every other "as of" figure lands on
  // that month, not the one before it); the month in progress is read as it stands today.
  const dateForMonth = (fy, m) => {
    const after = dayAfterMonth(m >= 3 ? fy : fy + 1, m);
    return isoOf(after > todayDate ? todayDate : after);
  };
  const monthStarted = (fy, m) => new Date(m >= 3 ? fy : fy + 1, m, 1) <= todayDate;
  const bucketIsKey = BUCKETS.some((k) => k.key === bucket);
  const maxWip = Math.max(1, ...data.wip.map((w) => w.count));
  const slowest = Math.max(...data.stage_stats.map((x) => x.avg || 0));

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="daily-header">
      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1220] via-[#101a2e] to-[#1a2a47] text-white" data-testid="daily-summary">
        <div className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full bg-[#FF5A00]/25 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative px-4 sm:px-6 lg:px-8 pt-6 sm:pt-7 pb-6">
          <div className="flex flex-wrap items-start gap-4" data-testid="daily-asof-note">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#FF8A4C] font-semibold">Day-to-day control</div>
              <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight mt-1">Daily Dashboard</h1>
              <p className="mt-2 text-sm text-slate-300 max-w-2xl">
                <b className="text-white">{b.today}</b> due today
                <span className="mx-2 text-slate-500">·</span><b className="text-white">{lateTotal}</b> overdue
                {data.risk?.late_value > 0 && <>
                  <span className="mx-2 text-slate-500">·</span>
                  <b className="text-rose-300">{formatINR(data.risk.late_value)}</b> of orders late
                </>}
                {escCount > 0 && <>
                  <span className="mx-2 text-slate-500">·</span>
                  <b className="text-orange-300">{escCount} escalated</b> (idle {data.escalate_days}+ days)
                </>}
                {risk.jobs > 0 && <>
                  <span className="mx-2 text-slate-500">·</span>
                  <b className="text-amber-300">{risk.customers} Gold / Silver customer{risk.customers === 1 ? "" : "s"}</b> waiting on a late job
                </>}
              </p>
            </div>

            <div className="w-full sm:w-auto sm:ml-auto flex flex-col items-start sm:items-end gap-2">
              <div className="flex items-center gap-4 text-right">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">Data as of</div>
                  <div className="font-display font-bold text-xl leading-tight">{formatDate(data.as_of)}</div>
                </div>
                <div className="h-8 w-px bg-white/15" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">System date</div>
                  <div className="font-display font-bold text-xl leading-tight text-slate-300">{formatDate(data.today)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-full bg-white/10 p-1 ring-1 ring-white/10" role="group" aria-label="Board date">
                  <button onClick={() => { setPickedMonth(null); setAsOf(data.latest_data_date); }}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${!viewingToday ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}>
                    Latest data
                  </button>
                  <button onClick={() => { setPickedMonth(null); setAsOf(data.today); }}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${viewingToday ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}>
                    Switch to today
                  </button>
                </div>
                <Input type="date" value={asOf} onChange={(e) => { setPickedMonth(null); setAsOf(e.target.value); }} data-testid="daily-asof"
                       className="w-36 h-8 rounded-full bg-white/10 border-white/15 text-white text-xs [color-scheme:dark]" />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={downloadPdf} className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold ring-1 ring-white/15 transition hover:bg-white/20">
                  <Download size={13} /> Export PDF
                </button>
                {canDigest && (
                  <button onClick={openDigest} className="flex items-center gap-1.5 rounded-full bg-[#FF5A00] px-3 py-1.5 text-xs font-semibold transition hover:bg-[#ff7424]">
                    <Mail size={13} /> Morning digest
                  </button>
                )}
              </div>
              {data.using_latest_data && !viewingToday && (
                <span className="text-[11px] text-sky-200">Your data ends before today, so the board shows the latest data date.</span>
              )}
            </div>
          </div>

          {/* ===== Financial year and month ===== */}
          <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="daily-fy-picker">
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Financial year</span>
            <select value={asOfFy} onChange={(e) => { const fy = Number(e.target.value); setPickedMonth({ fy, m: asOfMonth }); setAsOf(dateForMonth(fy, asOfMonth)); }}
                    data-testid="daily-fy" aria-label="Financial year"
                    className="h-8 rounded-full bg-white/10 px-3 text-xs font-semibold text-white ring-1 ring-inset ring-white/15 [color-scheme:dark]">
              {fyYears.map((y) => <option key={y} value={y} className="text-slate-900">{fyLabel(y)}</option>)}
            </select>
            <div className="h-5 w-px bg-white/15 hidden sm:block" />
            <div className="flex flex-wrap gap-1" role="group" aria-label="Month">
              {FY_MONTHS.map((m) => {
                const on = asOfMonth === m;
                const can = monthStarted(asOfFy, m);
                return (
                  <button key={m} type="button" disabled={!can} onClick={() => { setPickedMonth({ fy: asOfFy, m }); setAsOf(dateForMonth(asOfFy, m)); }}
                          data-testid={`daily-month-${m}`} title={can ? `${MONTHS[m]} ${m >= 3 ? asOfFy : asOfFy + 1}` : "Not started yet"}
                          className={`h-7 rounded-full px-2.5 text-[11px] font-semibold transition disabled:opacity-25 disabled:cursor-not-allowed ${
                            on ? "bg-white text-slate-900" : "bg-white/5 text-slate-300 ring-1 ring-inset ring-white/10 hover:bg-white/15 hover:text-white"}`}>
                    {MONTHS[m]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {BUCKETS.map((k) => {
              const on = bucket === k.key;
              return (
                <button key={k.key} onClick={() => setBucket(on ? "active" : k.key)} data-testid={`bucket-${k.key}`}
                        className={`group text-left rounded-xl p-4 backdrop-blur transition ring-1 ${on ? "bg-white/15 ring-white/60" : "bg-white/5 ring-white/10 hover:bg-white/10"}`}>
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-300 font-medium">
                    <span className={`h-2 w-2 rounded-full ${k.dot}`} /> {k.label}
                  </div>
                  <div className="mt-2 flex items-end justify-between">
                    <span className="font-mono-num text-4xl font-bold leading-none">{b[k.key]}</span>
                    <span className="text-[11px] text-slate-400 font-mono-num">{Math.round(((b[k.key] || 0) / flagged) * 100)}%</span>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex h-1.5 overflow-hidden rounded-full bg-white/10 gap-0.5" title="Share of flagged jobs by urgency">
            {BUCKETS.map((k) => <div key={k.key} className={k.dot} style={{ width: `${((b[k.key] || 0) / flagged) * 100}%` }} />)}
          </div>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8 py-5 sm:py-6 space-y-6">
        {/* ===== Performance strip ===== */}
        <Collapse id="perf" testId="daily-performance" title="Plant performance"
                  summary={`Good output ${perf.current.good_pct ?? "-"}% · Wastage ${perf.current.wastage_pct ?? "-"}% · On-time ${otif4 ?? "-"}%`}>
          <div className="mb-3 text-xs text-slate-500">
            {hasPerf ? `Last 30 days, ${perf.current.records} printing records, change against the 30 days before` : "No printing records in the last 30 days"}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {tiles.map((t) => {
              const delta = t.value != null && t.prev != null ? Math.round((t.value - t.prev) * 10) / 10 : null;
              const good = delta != null && delta !== 0 && (t.better === "up" ? delta > 0 : delta < 0);
              return (
                <Link key={t.label} to={t.to} className={`${CARD} group p-4 transition hover:-translate-y-0.5 hover:shadow-md`}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{t.label}</div>
                  <div className="mt-1 flex items-end gap-2">
                    <span className="font-mono-num text-3xl font-bold leading-none text-slate-900">{t.value != null ? `${t.value}${t.unit}` : "-"}</span>
                    {delta != null && delta !== 0 && (
                      <span className={`mb-0.5 inline-flex items-center text-xs font-semibold ${good ? "text-emerald-600" : "text-red-600"}`}>
                        {delta > 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}{Math.abs(delta)}{t.unit}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
                    <span>{t.sub}</span>
                    <span className="flex items-center gap-0.5 font-semibold opacity-0 transition group-hover:opacity-100 group-hover:text-[#FF5A00]">Report <ArrowRight size={11} /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </Collapse>

        {/* ===== Jobs ===== */}
        <section className={CARD} data-testid="daily-jobs">
          <div className={`p-4 sm:p-5 space-y-4 ${jobsOpen ? "border-b border-slate-100" : ""}`}>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={toggleJobs} aria-expanded={jobsOpen}
                      className="font-display font-bold text-xl mr-auto flex items-center gap-2 text-slate-900">
                {jobsOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <CalendarClock size={20} className="text-[#FF5A00]" /> Jobs needing attention
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-600 font-mono-num">
                  {showingSearch ? (searchHits?.total ?? "…") : jobs.length}
                </span>
              </button>
              {!jobsOpen && <span className="text-xs text-slate-500">{b.today} due today · {lateTotal} overdue · {escCount} escalated</span>}
              <div className={`relative w-full sm:w-auto ${jobsOpen ? "" : "hidden"}`}>
                <Search size={14} className="absolute left-3 top-3 text-slate-400" />
                <Input placeholder="Search job, customer, product" value={q} onChange={(e) => setQ(e.target.value)}
                       className="w-full sm:w-72 h-10 rounded-full pl-9 bg-slate-50 border-slate-200" />
              </div>
            </div>

            <div className={`grid gap-3 ${jobsOpen ? "" : "hidden"}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="label-caps w-20">Customer</span>
                {Object.keys(TIER).map((t) => (
                  <button key={t} onClick={() => toggleTier(t)} data-testid={`tier-toggle-${t}`}
                          className={`rounded-full transition ${tiers.includes(t) ? "ring-2 ring-slate-900 ring-offset-2" : "opacity-70 hover:opacity-100"}`}>
                    <TierChip tier={t} className="px-3 py-1" />
                  </button>
                ))}
                {tiers.length > 0 && <button className="text-xs underline text-slate-500" onClick={() => setTiers([])}>Clear</button>}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="label-caps w-20">Stuck at</span>
                <button className={pill(stage === "all")} onClick={() => setStage("all")}>All <span className="font-mono-num ml-1 opacity-70">{sliced.length}</span></button>
                {STAGE_FILTERS.map((s) => (
                  <button key={s.key} onClick={() => setStage(stage === s.key ? "all" : s.key)}
                          disabled={!stageCounts[s.key] && stage !== s.key}
                          className={`${pill(stage === s.key)} disabled:opacity-40 disabled:hover:ring-slate-200`}>
                    {s.label} <span className="font-mono-num ml-1 opacity-70">{stageCounts[s.key] || 0}</span>
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="label-caps w-20">Owner</span>
                <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}
                        className="h-8 rounded-full bg-white px-3 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
                  <option value="all">Everyone</option>
                  <option value="unassigned">Unassigned</option>
                  {owners.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="label-caps w-20">Age</span>
                {[["active", "Active bottlenecks"], ["stale", "Stale > 30 d"], ["all", "Everything"]].map(([k, l]) => (
                  <button key={k} onClick={() => setBucket(k)} className={pill(bucket === k)}>{l}</button>
                ))}
                <button onClick={() => setOnlyEsc(!onlyEsc)} className={`${pill(onlyEsc)} flex items-center gap-1`}>
                  <AlertOctagon size={12} /> Escalated <span className="font-mono-num opacity-70">{escCount}</span>
                </button>
                {materialFilter && (
                  <span className="rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
                    Waiting on {data.material_blockers.find((m) => m.key === materialFilter)?.name || materialFilter}
                    <button className="ml-2 underline" onClick={() => setMaterialFilter(null)}>clear</button>
                  </span>
                )}
                {bucketIsKey && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
                    {BUCKETS.find((k) => k.key === bucket).label}
                    <button className="ml-2 underline" onClick={() => setBucket("active")}>clear</button>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* tiles */}
          {showingSearch ? (
            <div className={`p-3 sm:p-5 ${jobsOpen ? "" : "hidden"} bg-slate-50/60 rounded-b-xl`}>
              <div className="mb-3 flex items-center gap-2 text-xs text-slate-500">
                <Search size={13} />
                {searching ? "Searching every job…" : searchHits
                  ? <>Not in the current filters. Found by search: <b className="text-slate-800">{searchHits.total}</b> job{searchHits.total === 1 ? "" : "s"}{searchHits.total > searchHits.jobs.length ? `, showing the first ${searchHits.jobs.length}` : ""}.</>
                  : null}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
                {(searchHits?.jobs || []).map((j) => (
                  <JobCard key={j.id} j={j} escalateDays={data.escalate_days} onClick={() => selectJob(j)} />
                ))}
                {!searching && searchHits && searchHits.jobs.length === 0 && (
                  <div className="col-span-full p-10 text-center text-sm text-slate-500">No job matches "{searchTerm}" — check the BPR, job number, customer, product, PO or invoice number.</div>
                )}
              </div>
            </div>
          ) : (
            <div className={`p-3 sm:p-5 grid ${jobsOpen ? "" : "hidden"} grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 bg-slate-50/60 rounded-b-xl`}>
              {pageJobs.map((j) => <JobCard key={j.id} j={j} escalateDays={data.escalate_days} onClick={() => selectJob(j)} />)}
              {jobs.length === 0 && <div className="col-span-full p-10 text-center text-sm text-slate-500">Nothing here. All clear for this filter.</div>}
            </div>
          )}

          {jobsOpen && jobs.length > PAGE && (
            <div className="p-4 flex items-center justify-between border-t border-slate-100 text-sm">
              <span className="text-slate-500 font-mono-num">{(page - 1) * PAGE + 1}–{Math.min(page * PAGE, jobs.length)} of {jobs.length}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                <span className="font-mono-num text-slate-600">Page {page} / {pages}</span>
                <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            </div>
          )}
        </section>

        {/* ===== Throughput ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-6">
          <Collapse id="wip" testId="daily-wip" title="Open jobs by stage" summary={`${wipTotal} open · ${wipLate} late`}>
            <div className="text-xs text-slate-500 mb-4">Where the work is sitting right now, and how much of it is already late.</div>
            <div className="space-y-3">
              {data.wip.map((w) => (
                <div key={w.key}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="font-medium text-slate-700">{w.label}</span>
                    <span className="font-mono-num text-slate-900 font-semibold">
                      {w.count}
                      {w.overdue > 0 && <span className="ml-2 text-xs font-medium text-red-600">{w.overdue} late</span>}
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden flex">
                    <div className="bg-slate-800" style={{ width: `${((w.count - w.overdue) / maxWip) * 100}%` }} />
                    <div className="bg-red-500" style={{ width: `${(w.overdue / maxWip) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Collapse>

          <Collapse id="stagechart" testId="daily-stage-chart" title="Average days per stage"
                    summary={data.avg_total_days != null ? `Order to dispatch ${data.avg_total_days} days` : null}>
            <div className="text-xs text-slate-500 mb-2">
              From {data.completed_sample} jobs dispatched in the last 180 days
              {data.avg_total_days != null && <> · order to dispatch: <b className="text-slate-800">{data.avg_total_days} days</b></>}
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.stage_stats.filter((s) => s.avg != null)} margin={{ left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="avg" name="Avg days" radius={[4, 4, 0, 0]}>
                    {data.stage_stats.filter((s) => s.avg != null).map((s) => (
                      <Cell key={s.key} fill={s.avg === slowest ? "#FF5A00" : "#334155"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11px] text-slate-500">The orange bar is the slowest stage on average.</div>
          </Collapse>
        </div>

        {/* ===== Delivery and wastage trends ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-6">
          <Collapse id="otif" testId="daily-otif" title="On-time delivery by week" summary={otif4 != null ? `${otif4}% over the last 4 weeks` : null}>
            <div className="text-xs text-slate-500 mb-2">Share of dispatched jobs shipped on or before the promised date, last 12 weeks.</div>
            {otifHas ? (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={otif.map((w) => ({ ...w, label: formatDate(w.week).slice(0, 6) }))} margin={{ left: -20, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v, n, ctx) => [`${v}% (${ctx.payload.on_time} of ${ctx.payload.total} jobs)`, "On time"]} />
                    <Line type="monotone" dataKey="pct" stroke="#FF5A00" strokeWidth={2.5} dot={{ r: 3, fill: "#FF5A00", stroke: "#fff", strokeWidth: 2 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="py-8 text-center text-sm text-slate-400">No dispatched jobs with a promised date in the last 12 weeks.</div>}
          </Collapse>

          <Collapse id="wastage" testId="daily-wastage" title="Wastage by week" summary={lastWaste ? `${lastWaste.wastage_pct}% latest week` : null}>
            <div className="text-xs text-slate-500 mb-2">Share of metres run that were wasted (total counter minus good counter), last 12 weeks. Lower is better.</div>
            {wasteHas ? (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={perf.wastage_weekly.map((w) => ({ ...w, label: formatDate(w.week).slice(0, 6) }))} margin={{ left: -20, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis unit="%" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v, n, ctx) => [`${v}% (${ctx.payload.records} records)`, "Wastage"]} />
                    <Line type="monotone" dataKey="wastage_pct" stroke="#334155" strokeWidth={2.5} dot={{ r: 3, fill: "#334155", stroke: "#fff", strokeWidth: 2 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : <div className="py-8 text-center text-sm text-slate-400">No printing records with counters in the last 12 weeks.</div>}
          </Collapse>
        </div>

        {/* ===== Churn and material blockers ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-6">
          <Collapse id="churn" testId="daily-churn" title="Customer churn"
                    icon={<UserMinus size={18} className={churn.dropped > 0 ? "text-rose-600" : "text-emerald-600"} />}
                    summary={churn.partial ? "Sales not imported to the end of the month" : churn.dropped > 0 ? `${churn.dropped} of ${churn.baseline_customers} fell 50%+` : "No drops"}>
            <div className="flex items-start gap-3">
              <div className="flex-1 text-xs text-slate-500">Billing in {monthName(churn.current[0])} against {monthName(churn.baseline[0])}, two full months.</div>
              <Link to="/customers" className="text-xs font-semibold uppercase tracking-widest text-[#FF5A00] hover:underline">Full report →</Link>
            </div>
            {churn.partial ? (
              <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-inset ring-amber-200">
                Sales invoices for {monthName(churn.current[0])} stop on {formatDate(churn.last_invoice)}, so a comparison against{" "}
                {monthName(churn.baseline[0])} would show every customer falling. Import the rest of the month's sales register to read churn here.
              </div>
            ) : churn.dropped === 0 ? (
              <div className="mt-4 text-sm text-slate-500">No customer fell by 50% or more. Billed customers in {monthName(churn.baseline[0])}: {churn.baseline_customers}.</div>
            ) : (
              <>
                <div className="mt-3 text-sm text-slate-700">
                  <b className="font-mono-num text-slate-900">{churn.dropped}</b> of {churn.baseline_customers} customers fell by 50% or more,
                  <b className="font-mono-num text-slate-900"> {churn.stopped}</b> stopped billing altogether.
                </div>
                <div className="mt-3 divide-y divide-slate-100">
                  {churn.top.map((c) => (
                    <div key={c.name} className="flex items-center gap-2 py-1.5 text-sm">
                      <TierChip tier={c.tier} />
                      <span className="flex-1 truncate" title={c.name}>{c.name}</span>
                      {c.stopped && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-700 ring-1 ring-inset ring-rose-200">Stopped</span>}
                      {c.lost != null && <span className="font-mono-num text-xs text-slate-500">-{formatINR(c.lost)}</span>}
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[11px] text-slate-400">Gold and Silver customers are listed first.</div>
              </>
            )}
          </Collapse>

          <Collapse id="materials" testId="daily-material-blockers" title="Materials holding up late jobs"
                    icon={<Boxes size={18} className={data.blocked_jobs > 0 ? "text-amber-600" : "text-emerald-600"} />}
                    summary={data.blocked_jobs > 0 ? `${data.blocked_jobs} job(s) blocked` : "None blocked"}>
            <div className="text-xs text-slate-500">Jobs that are late or due within 3 days and flagged as missing material, or still unplanned with zero stock.</div>
            {data.material_blockers.length === 0 ? (
              <div className="mt-4 text-sm text-slate-500">No late job is waiting on material.</div>
            ) : (
              <div className="mt-3 space-y-2">
                <div className="text-sm text-slate-700"><b className="font-mono-num text-slate-900">{data.blocked_jobs}</b> job(s) across {data.material_blockers.length} material(s).</div>
                {data.material_blockers.map((m) => (
                  <button key={m.key} onClick={() => { setMaterialFilter(m.key); setBucket("all"); document.querySelector('[data-testid="daily-jobs"]')?.scrollIntoView({ behavior: "smooth" }); }}
                          className={`w-full rounded-lg p-3 text-left ring-1 ring-inset transition hover:bg-slate-50 ${materialFilter === m.key ? "ring-slate-900" : "ring-slate-200"}`}>
                    <div className="flex items-center gap-2">
                      <span className="flex-1 truncate font-semibold text-sm text-slate-900" title={m.name}>{m.name || m.key}</span>
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-800 ring-1 ring-inset ring-amber-200">{m.jobs} job{m.jobs === 1 ? "" : "s"}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {m.key} · {m.customers} customer{m.customers === 1 ? "" : "s"} · {m.in_stock_master
                        ? <>stock <b className={m.stock <= (m.reorder || 0) ? "text-red-600" : "text-slate-700"}>{m.stock} {m.unit}</b>{m.reorder != null && <> (reorder at {m.reorder})</>}</>
                        : "not in the stock master"}
                    </div>
                    <div className="mt-1 truncate font-mono-num text-[11px] text-slate-400">{m.bprs.join(", ")}{m.jobs > m.bprs.length ? ` +${m.jobs - m.bprs.length} more` : ""}</div>
                  </button>
                ))}
              </div>
            )}
          </Collapse>
        </div>

        {/* ===== Customer tiers (collapsed) ===== */}
        <section className={CARD} data-testid="daily-customers">
          <button className="w-full p-5 flex items-center gap-3 text-left" onClick={() => setShowTiers(!showTiers)}>
            {showTiers ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span className="font-display font-bold text-lg text-slate-900">Customers by tier</span>
            <span className="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
              {Object.keys(TIER).map((t) => (
                <span key={t} className="flex items-center gap-1.5"><TierChip tier={t} /><span className="font-mono-num text-sm font-semibold">{data.tier_counts[t]}</span></span>
              ))}
            </span>
          </button>
          {showTiers && (
            <div className="border-t border-slate-100">
              <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs max-w-xl">
                <label>Gold top %
                  <Input type="number" min="1" max="50" value={cfg.gold_pct} className="h-8 mt-1"
                         onChange={(e) => setCfg({ ...cfg, gold_pct: Number(e.target.value) || 10 })} />
                </label>
                <label>Silver top %
                  <Input type="number" min="1" max="80" value={cfg.silver_pct} className="h-8 mt-1"
                         onChange={(e) => setCfg({ ...cfg, silver_pct: Number(e.target.value) || 30 })} />
                </label>
                <label>New within (days)
                  <Input type="number" min="1" max="365" value={cfg.new_days} className="h-8 mt-1"
                         onChange={(e) => setCfg({ ...cfg, new_days: Number(e.target.value) || 30 })} />
                </label>
                <p className="col-span-3 text-[11px] text-slate-500">
                  Gold and Silver are the top customers by revenue or by number of jobs. New means the first order arrived within the window.
                </p>
              </div>
              <div className="px-5 pb-3 flex gap-2">
                {["all", ...Object.keys(TIER)].map((t) => (
                  <button key={t} onClick={() => setCustTier(t)} className={pill(custTier === t)}>{t === "all" ? "All" : TIER[t].label}</button>
                ))}
              </div>
              <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-100">
                {data.customers.filter((c) => custTier === "all" || c.tier === custTier).map((c) => (
                  <div key={c.name} className={`px-5 py-2 flex items-center gap-2 text-sm ${(TIER[c.tier] || TIER.regular).row}`}>
                    <TierChip tier={c.tier} />
                    <span className="truncate flex-1" title={c.name}>{c.name}</span>
                    {c.due_today > 0 && <span className="rounded-full text-[11px] px-2 bg-rose-600 text-white">{c.due_today} due</span>}
                    <span className="text-xs text-slate-500 font-mono-num whitespace-nowrap">
                      {c.jobs} jobs{c.revenue != null ? ` · ${formatINR(c.revenue)}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Morning digest</DialogTitle></DialogHeader>
          {!digest ? <div className="p-6 text-sm text-slate-500">Building preview…</div> : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`rounded-full px-2.5 py-1 font-semibold ring-1 ring-inset ${digest.channels.email ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-slate-50 text-slate-500 ring-slate-200"}`}>Email {digest.channels.email ? "on" : "not set up"}</span>
                <span className={`rounded-full px-2.5 py-1 font-semibold ring-1 ring-inset ${digest.channels.webhook ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-slate-50 text-slate-500 ring-slate-200"}`}>Chat / WhatsApp webhook {digest.channels.webhook ? "on" : "not set up"}</span>
                <span className="text-slate-500">Goes out daily at {String(digest.hour).padStart(2, "0")}:00 to {digest.recipients.length ? digest.recipients.join(", ") : "no recipients"}</span>
              </div>
              <iframe title="Digest preview" srcDoc={digest.html} sandbox="" className="h-80 w-full rounded-lg border border-slate-200 bg-white" />
              {!digest.channels.email && !digest.channels.webhook && (
                <p className="text-xs text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 rounded-lg px-3 py-2">
                  To enable sending, set SMTP_HOST (with SMTP_USER, SMTP_PASS, SMTP_FROM) or DIGEST_WEBHOOK_URL in the backend environment and restart it.
                </p>
              )}
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDigestOpen(false)}>Close</Button>
            <Button onClick={sendDigest} disabled={sending || !digest || (!digest.channels.email && !digest.channels.webhook)} className="bg-[#FF5A00] hover:bg-[#e65100] text-white">
              <Send size={14} className="mr-1" /> Send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Drill-down drawer ===== */}
      <Sheet open={!!sel} onOpenChange={(o) => !o && setSelId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto p-0 bg-slate-50" data-testid="job-drawer">
          {sel && (
            <>
              <SheetHeader className="relative overflow-hidden bg-gradient-to-br from-[#0B1220] via-[#101a2e] to-[#1a2a47] px-4 sm:px-6 pt-7 pb-5 text-white space-y-1">
                <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#FF5A00]/25 blur-3xl" />
                <SheetTitle className="relative flex flex-wrap items-center gap-3 text-white">
                  <span className="font-mono-num text-2xl font-bold">{sel.bpr}</span> <TierChip tier={sel.tier} />
                </SheetTitle>
                <SheetDescription className="relative text-slate-300">
                  <span className="text-white font-semibold">{sel.customer}</span><br />
                  {sel.product} · <b className="text-white">{Number(sel.qty || 0).toLocaleString("en-IN")} Qty</b>
                </SheetDescription>
                <div className="relative pt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-slate-300">Due {formatDate(sel.due_date)}</span> <DueBadge days={sel.days_to_due} />
                  <span className="rounded-full bg-white/10 ring-1 ring-white/15 px-3 py-1 font-semibold">
                    At: {sel.current_label}{sel.waiting_for ? ` · waiting for ${sel.waiting_for}` : ""}
                  </span>
                  <span className="text-slate-300">Idle {sel.days_since_update ?? "-"}d · {sel.total_days ?? "-"}d since order</span>
                  <Link to={`/orders/${sel.id}`} className="ml-auto mr-8 flex items-center gap-1 text-slate-200 underline"><ExternalLink size={12} /> Open order</Link>
                </div>
              </SheetHeader>

              <div className="p-4 sm:p-6 space-y-6">
                <section className={`${CARD} p-4 flex flex-wrap items-center gap-3`}>
                  <UserRound size={16} className="text-slate-400" />
                  <span className="label-caps">Owner</span>
                  <select value={sel.owner || ""} onChange={(e) => assignOwner(sel, e.target.value)}
                          className="h-9 rounded-full bg-white px-3 text-sm ring-1 ring-inset ring-slate-300">
                    <option value="">Unassigned</option>
                    {owners.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {sel.value > 0 && <span className="ml-auto text-sm text-slate-500">Order value <b className="font-mono-num text-slate-900">{formatINR(sel.value)}</b></span>}
                  {sel.escalated && <span className="flex items-center gap-1 text-xs font-semibold text-orange-700"><AlertOctagon size={13} /> Escalated: late and idle {sel.days_since_update}d</span>}
                </section>

                <section>
                  <div className="label-caps mb-2">Order flowchart</div>
                  <OrderFlowchart jobId={sel.id} asOf={asOf} />
                </section>

                <section className={`${CARD} p-4`}>
                  <div className="label-caps mb-2">Plan vs actual by stage</div>
                  {STAGE_ORDER.some((k) => sel.stage_days[k] != null) ? (
                    <div className="overflow-x-auto"><table className="w-full min-w-[360px] text-sm font-mono-num">
                      <thead><tr className="text-xs text-slate-500 text-left">
                        <th className="font-normal">Stage</th><th className="font-normal text-right">Actual</th>
                        <th className="font-normal text-right">Typical</th><th className="font-normal text-right">Difference</th>
                      </tr></thead>
                      <tbody>
                        {STAGE_ORDER.filter((k) => sel.stage_days[k] != null).map((k) => {
                          const d = sel.stage_days[k];
                          const diff = avg[k] != null ? Math.round((d - avg[k]) * 10) / 10 : null;
                          const slow = avg[k] != null && d > Math.max(2, avg[k] * 1.5);
                          return (
                            <tr key={k} className={`border-t border-slate-100 ${slow ? "text-red-700 font-bold" : ""}`}>
                              <td className="py-1.5 font-sans">{STAGE_SHORT[k]}</td>
                              <td className="text-right">{d}d</td>
                              <td className="text-right text-slate-500">{avg[k] != null ? `${avg[k]}d` : "-"}</td>
                              <td className="text-right">{diff != null ? `${diff > 0 ? "+" : ""}${diff}d` : "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table></div>
                  ) : <div className="text-xs text-slate-400">No stage timings recorded yet</div>}
                </section>

                <section className={`${CARD} p-4`}>
                  <div className="label-caps mb-2">Why is it delayed?</div>
                  <div className="flex flex-wrap gap-1.5">
                    {sel.auto_reasons.map((r) => (
                      <span key={r} className="rounded-full px-2.5 py-1 text-xs bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200">{r}</span>
                    ))}
                  </div>
                  {sel.manual_reason && (
                    <div className="mt-2 rounded-lg text-xs text-sky-900 bg-sky-50 ring-1 ring-inset ring-sky-200 px-3 py-2">
                      Current reason: <b>{sel.manual_reason}</b>
                      <span className="text-slate-500"> · {sel.manual_reason_by || ""} {sel.manual_reason_at ? formatDate(sel.manual_reason_at) : ""}</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {REASON_PRESETS.map((r) => (
                      <button key={r} className="rounded-full text-xs px-3 py-1 ring-1 ring-inset ring-slate-200 hover:ring-slate-400 hover:bg-slate-50"
                              onClick={() => setReasonText(r)}>{r}</button>
                    ))}
                  </div>
                  <Textarea className="mt-3" rows={3} value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Type the reason" />
                  <div className="mt-3 flex justify-end">
                    <Button onClick={saveReason} disabled={saving || !reasonText.trim()} className="rounded-full bg-[#FF5A00] hover:bg-[#e65100] text-white">
                      <MessageSquarePlus size={14} className="mr-1" /> {sel.manual_reason ? "Update reason" : "Add reason"}
                    </Button>
                  </div>
                </section>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
